// Métricas del panel INICIO del scout.
//
// Antes se leían de `scout_tracking.metrics` (talent_funnel / top_sports), un
// blob que nadie escribe: cualquier scout nuevo — incluido el revisor de Apple —
// veía 0 / 0 / 0 y "Aún no hay datos de deportes" aunque la plataforma tuviera
// 88 atletas. Estas funciones derivan las mismas cifras de datos vivos: la lista
// de atletas que ya carga el AuthContext y las solicitudes de contacto del scout.

const NEW_TALENT_WINDOW_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Embudo de talentos, calculado sobre datos reales:
 *  - Nuevos:      atletas registrados en los últimos 30 días.
 *  - Evaluando:   atletas con perfil completo que el scout aún no ha contactado.
 *  - Contactados: atletas a los que este scout ya envió una solicitud.
 *
 * @param {Array} athletes lista normalizada (ver AuthContext.normalizeAthleteRecord)
 * @param {Object} contactRequestsByAthleteId mapa { [athleteId]: solicitud }
 */
export const deriveTalentFunnel = (
  athletes = [],
  contactRequestsByAthleteId = {},
  now = Date.now(),
) => {
  const list = Array.isArray(athletes) ? athletes : [];
  const contactedIds = new Set(
    Object.keys(contactRequestsByAthleteId || {}).map(String),
  );
  const cutoff = now - NEW_TALENT_WINDOW_DAYS * DAY_MS;

  const newTalents = list.filter((athlete) => {
    const created = athlete?.created_at ? Date.parse(athlete.created_at) : NaN;
    return Number.isFinite(created) && created >= cutoff;
  }).length;

  const evaluating = list.filter(
    (athlete) =>
      athlete?.profile_complete && !contactedIds.has(String(athlete?.id)),
  ).length;

  return {
    new_talents: newTalents,
    evaluating,
    contacted: contactedIds.size,
  };
};

/**
 * Distribución de atletas por deporte, en el formato que ya consume la barra de
 * progreso del panel: `{ sport, score, max_score }`. `max_score` es el total de
 * atletas con deporte, de modo que las barras son porcentajes reales del padrón.
 */
export const deriveTopSports = (athletes = [], limit = 4) => {
  const list = Array.isArray(athletes) ? athletes : [];
  const counts = new Map();

  for (const athlete of list) {
    const sport = athlete?.sport;
    if (!sport) continue;
    counts.set(sport, (counts.get(sport) || 0) + 1);
  }

  const total = [...counts.values()].reduce((sum, count) => sum + count, 0);
  if (total === 0) return [];

  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([sport, score]) => ({ sport, score, max_score: total }));
};

// deriveAverageRatings (Ratings panel) se quitó junto con el panel: lo que
// significa `athletes.points` y su escala real quedó pendiente de decisión
// de producto. Recuperable en el historial de git si se retoma.
